import { useState, useMemo, useEffect } from 'react';
import { AlertTitle } from '@chakra-ui/react';
import { useSelector, useDispatch } from 'react-redux';
import {
    Box,
    ChakraProvider,
    FormControl,
    ButtonGroup,
    HStack,
} from '@chakra-ui/react';
import { ResponsiveBarCanvas } from '@nivo/bar';

import { DateRangePicker } from 'rsuite';
import 'rsuite/dist/rsuite.min.css';
import Button from 'components/v4/Button';
import { Card } from 'components/v4/Card';
import { Alert, AlertIcon, Text } from '@/components/v4';

import walkthroughIds from 'pages/dashboards/walkthroughIds';
import chakratheme from '@/chakratheme';

// theme from material ui
const theme = {
    textColor: '#ffffff',
    fontSize: 11,
    axis: {
        domain: {
            line: {
                stroke: '#777777',
                strokeWidth: 1,
            },
        },
        ticks: {
            line: {
                stroke: '#777777',
                strokeWidth: 3,
            },
        },
    },
    grid: {
        line: {
            stroke: '#dddddd',
            strokeWidth: 1,
        },
    },
};

/*
 * Component for the Call Stats page of the dashboard
 */
const CallDashboard = () => {
    const today = new Date();
    const dispatch = useDispatch();
    const lastMonth = new Date(
        today.getFullYear(),
        today.getMonth() - 1,
        today.getDate(),
    );
    const [from, setFrom] = useState('');
    const [to, setTo] = useState('');
    const [mode, setMode] = useState('Calls');
    const [grouping, setGrouping] = useState('Daily');
    const [selectedDates, setSelectedDates] = useState([lastMonth, today]);

    const {
        datePicker,
        searchButton,
        groupingSelect,
        metricsSelect,
        callStatsChart,
    } = walkthroughIds.dashboardsCallStats;

    // table data from state
    const { state: tableState, loading: tableLoading } = useSelector(
        (state) => {
            return { ...state.table };
        },
    );

    // settings data from state
    const { darkMode } = useSelector((state) => {
        return { ...state.settings };
    });

    // navigation data from state
    const { currentCompany } = useSelector((state) => {
        return { ...state.navigationLists };
    });

    // colors for the graph
    const colors = {
        'Inbound (calls)': '#E0F9B5',
        'Outbound (calls)': '#A5DEE5',
        'Inbound (mins)': '#E0F9B5',
        'Outbound (mins)': '#A5DEE5',
    };

    // function to get the color for each bar
    const getColor = (bar) => colors[bar.id];

    // useMemo to get the data from the table as its a large data set
    const data = useMemo(() => {
        const init = [
            {
                'Outbound (mins)': 0,
                'Inbound (mins)': 0,
                date: '',
                'Outbound (calls)': 0,
                'Inbound (calls)': 0,
            },
        ];
        if (tableState?.data?.length < 1) {
            return init;
        }

        return (
            tableState?.data
                ?.reduce((result, current) => {
                    const currentDate =
                        grouping === 'Daily'
                            ? current.dateTime?.substring(0, 10)
                            : current.dateTime?.substring(0, 7);
                    const index = result.findIndex((v) => {
                        return v.date === currentDate;
                    });
                    const currentDuration =
                        Number(current.duration?.substring(0, 2)) * 3600 +
                        Number(current.duration?.substring(3, 5)) * 60 +
                        Number(current.duration?.substring(6)); //seconds
                    const fieldDuration = `${current.direction} (mins)`;
                    const fieldNum = `${current.direction} (calls)`;

                    if (index === -1) {
                        result.push({
                            'Inbound (mins)': 0,
                            'Outbound (mins)': 0,
                            'Inbound (calls)': 0,
                            'Outbound (calls)': 0,
                            InboundColor: 'hsl(216, 70%, 50%)',
                            OutboundColor: 'hsl(216, 70%, 50%)',

                            date: currentDate,
                            [fieldDuration]: currentDuration,
                            [fieldNum]: 1,
                        });
                        return result;
                    } else {
                        result[index] = {
                            ...result[index],
                            [fieldDuration]:
                                result[index][fieldDuration] + currentDuration,
                            [fieldNum]: result[index][fieldNum] + 1,
                        };
                        return result;
                    }
                }, [])
                .map((v) => ({
                    ...v,
                    'Inbound (mins)': Number(v['Inbound (mins)'] / 60).toFixed(
                        2,
                    ),
                    'Outbound (mins)': Number(
                        v['Outbound (mins)'] / 60,
                    ).toFixed(2), //mins
                }))
                .sort((left, right) => {
                    return new Date(left.date) - new Date(right.date);
                }) ?? init
        );
    }, [tableState?.data, grouping]);

    useEffect(() => {
        if (
            selectedDates &&
            selectedDates.length === 2 &&
            selectedDates[0] &&
            selectedDates[1]
        ) {
            const fromDate = new Date(selectedDates[0]);
            const toDate = new Date(selectedDates[1]);

            // Set seconds and milliseconds to 0 for consistent timestamps
            fromDate.setSeconds(0, 0);
            toDate.setSeconds(0, 0);

            // Convert to UTC and format as yyyy-MM-ddTHH:mm:ssZ
            const fromISO = fromDate.toISOString().split('.')[0] + 'Z';
            const toISO = toDate.toISOString().split('.')[0] + 'Z';

            setFrom(fromISO);
            setTo(toISO);
        }
    }, [selectedDates]);

    // useEffect to refresh the dashboard
    useEffect(() => {
        if (!currentCompany || !from || !to) return;
        if (tableLoading) {
            dispatch({
                type: 'INIT_WEB_WORKER_STATE',
                payload: {
                    fetchURL: `/calldetailrecords/${currentCompany}/${encodeURIComponent(from)}/${encodeURIComponent(to)}`,
                    attribute: 'callDetailRecords',
                },
            });
        }
    }, [currentCompany, tableLoading, from, to]);

    // onClick to start the search for call records
    const onClick = () => {
        dispatch({
            type: 'INIT_WEB_WORKER_STATE',
            payload: {
                fetchURL: `/calldetailrecords/${currentCompany}/${encodeURIComponent(from)}/${encodeURIComponent(to)}`,
                attribute: 'callDetailRecords',
            },
        });
    };

    // if no company is selected, return a alert
    if (!currentCompany) {
        return (
            <Alert status="error">
                <AlertIcon />
                <AlertTitle>
                    No Partner / Company / Wholesaler Selected
                </AlertTitle>
                <Text>Please select a wholesaler and a partner / company.</Text>
            </Alert>
        );
    }

    // presentational aspect of the dashboard
    return (
        <>
            <ChakraProvider theme={chakratheme}>
                <Text
                    sx={{
                        fontSize: '20',
                        fontWeight: '500',
                        marginBottom: '15px',
                    }}>
                    {' '}
                    Calls Statistics
                </Text>

                {/* Controls Card */}
                <Card
                    borderRadius={15}
                    padding="25px 30px"
                    maxWidth={1200}
                    ml={0}
                    mb={4}
                    bg={darkMode ? 'dark.tcap' : null}>
                    <HStack spacing={3} align="center" flexWrap="nowrap">
                        <FormControl
                            data-walkthroughid={datePicker}
                            minWidth="300px">
                            <Box
                                sx={{
                                    '& .rs-input-group': {
                                        height: '32px !important',
                                    },
                                    '& .rs-picker-toggle': {
                                        height: '32px !important',
                                        minHeight: '32px !important',
                                    },
                                    '& .rs-picker-toggle-value': {
                                        lineHeight: '24px !important',
                                    },
                                }}>
                                <DateRangePicker
                                    value={selectedDates}
                                    onChange={setSelectedDates}
                                    format="MM/dd/yyyy HH:mm"
                                    placeholder="Select Date & Time Range"
                                    style={{ width: '100%' }}
                                    menuClassName={
                                        darkMode ? 'dark-rsuite-popup' : ''
                                    }
                                    cleanable={false}
                                    ranges={[
                                        {
                                            label: 'Today',
                                            value: [
                                                new Date(
                                                    new Date().setHours(
                                                        0,
                                                        0,
                                                        0,
                                                        0,
                                                    ),
                                                ),
                                                new Date(
                                                    new Date().setHours(
                                                        23,
                                                        59,
                                                        59,
                                                        999,
                                                    ),
                                                ),
                                            ],
                                        },
                                        {
                                            label: 'Yesterday',
                                            value: [
                                                new Date(
                                                    new Date().setDate(
                                                        new Date().getDate() -
                                                            1,
                                                    ),
                                                ).setHours(0, 0, 0, 0),
                                                new Date(
                                                    new Date().setDate(
                                                        new Date().getDate() -
                                                            1,
                                                    ),
                                                ).setHours(23, 59, 59, 999),
                                            ].map((date) => new Date(date)),
                                        },
                                        {
                                            label: 'Last 7 Days',
                                            value: [
                                                new Date(
                                                    new Date().setDate(
                                                        new Date().getDate() -
                                                            7,
                                                    ),
                                                ),
                                                new Date(),
                                            ],
                                        },
                                    ]}
                                />
                            </Box>
                        </FormControl>

                        <ButtonGroup
                            data-walkthroughid={groupingSelect}
                            isAttached
                            size="sm">
                            <Button
                                variant={
                                    grouping === 'Daily' ? 'solid' : 'outline'
                                }
                                onClick={() => setGrouping('Daily')}>
                                Daily
                            </Button>
                            <Button
                                variant={
                                    grouping === 'Monthly' ? 'solid' : 'outline'
                                }
                                onClick={() => setGrouping('Monthly')}>
                                Monthly
                            </Button>
                        </ButtonGroup>

                        <ButtonGroup
                            data-walkthroughid={metricsSelect}
                            isAttached
                            size="sm">
                            <Button
                                variant={mode === 'Calls' ? 'solid' : 'outline'}
                                onClick={() => setMode('Calls')}>
                                Calls
                            </Button>
                            <Button
                                variant={
                                    mode === 'Duration' ? 'solid' : 'outline'
                                }
                                onClick={() => setMode('Duration')}>
                                Duration
                            </Button>
                        </ButtonGroup>

                        <FormControl ml={0}>
                            <Button
                                onClick={onClick}
                                data-walkthroughid={searchButton}
                                size="sm">
                                Search
                            </Button>
                        </FormControl>
                    </HStack>
                </Card>

                {/* Chart Card */}
                <Card
                    borderRadius={15}
                    padding="30px"
                    maxWidth={1200}
                    ml={0}
                    bg={darkMode ? 'dark.tcap' : null}>
                    <div
                        style={{ width: '100%', height: '600px' }}
                        data-walkthroughid={callStatsChart}>
                        <ResponsiveBarCanvas
                            data={data}
                            keys={
                                mode === 'Calls'
                                    ? ['Inbound (calls)', 'Outbound (calls)']
                                    : ['Inbound (mins)', 'Outbound (mins)']
                            }
                            indexBy="date"
                            margin={{
                                top: 50,
                                right: 130,
                                bottom: 50,
                                left: 60,
                            }}
                            padding={0.3}
                            valueScale={{ type: 'linear' }}
                            indexScale={{ type: 'band', round: true }}
                            valueFormat={{ format: '', enabled: false }}
                            theme={darkMode ? theme : null}
                            tooltip={({ id, value, color }) => (
                                <div
                                    style={{
                                        padding: 12,
                                        color,
                                        background: '#222222',
                                    }}>
                                    {id}: {value}
                                </div>
                            )}
                            fill={[
                                {
                                    match: {
                                        id: 'Inbound (mins)',
                                    },
                                    id: 'Inbound (mins)',
                                },
                                {
                                    match: {
                                        id: 'Inbound (calls)',
                                    },
                                    id: 'Inbound (calls)',
                                },
                                {
                                    match: {
                                        id: 'Outbound (mins)',
                                    },
                                    id: 'Outbound (mins)',
                                },
                                {
                                    match: {
                                        id: 'Outbound (calls)',
                                    },
                                    id: 'Outbound (calls)',
                                },
                            ]}
                            defs={[
                                {
                                    id: 'Inbound (calls)',
                                    color: '#42ff21',
                                },
                                {
                                    id: 'Outbound (calls)',
                                    color: '#2596be',
                                },
                                {
                                    id: 'Inbound (mins)',
                                    background: '#42ff21',
                                },
                                {
                                    id: 'Outbound (mins)',
                                    color: '#2596be',
                                },
                            ]}
                            colors={getColor}
                            borderColor={{
                                from: 'color',
                                modifiers: [['darker', 3.0]],
                            }}
                            axisTop={null}
                            axisRight={null}
                            axisBottom={{
                                orient: 'bottom',
                                tickSize: 0,
                                tickPadding: 5,
                                tickRotation: 45,
                            }}
                            labelSkipWidth={12}
                            labelSkipHeight={12}
                            labelTextColor={{
                                from: 'color',
                                modifiers: [['darker', 3.0]],
                            }}
                            legends={[
                                {
                                    dataFrom: 'keys',
                                    anchor: 'top-right',
                                    direction: 'column',
                                    justify: false,
                                    translateX: 120,
                                    translateY: 0,
                                    itemsSpacing: 2,
                                    itemWidth: 100,
                                    itemHeight: 20,
                                    itemDirection: 'left-to-right',
                                    itemOpacity: 0.85,
                                    symbolSize: 20,
                                    effects: [
                                        {
                                            on: 'hover',
                                            style: {
                                                itemOpacity: 1,
                                            },
                                        },
                                    ],
                                },
                            ]}
                        />
                    </div>
                </Card>
            </ChakraProvider>
        </>
    );
};

export default CallDashboard;
